import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Grid, Header, Segment, Button, Icon, Modal, Message } from 'semantic-ui-react';
import ManagementHeader from '../../../common/components/ManagementHeader';
import { usePortfolioManagement, useSharedData } from '../../hooks/core/useZoneHooks';
import PortfoliosListEnh from '../lists/PortfoliosListEnh';
import PortfolioForm from '../forms/PortfolioForm';
import ActorSelector from '../common/ActorSelector';

/**
 * PortfolioManagement-enh component
 * Enhanced portfolio management component using zone-based architecture
 * @returns {JSX.Element} PortfolioManagement-enh component
 */
const PortfolioManagementEnh = () => {
  const dispatch = useDispatch();

  // Use zone-based hooks
  const portfolioManagement = usePortfolioManagement();
  const sharedData = useSharedData();

  // Local state
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [portfolioToDelete, setPortfolioToDelete] = useState(null);

  // Get current selections
  const selectedActorId = sharedData.selections?.actorId;
  const selectedPortfolioId = sharedData.selections?.portfolioId;
  const selectedPortfolio = portfolioManagement.portfolioProfiles?.[selectedPortfolioId] ||
    portfolioManagement.portfolios?.find(p => p.uuid === selectedPortfolioId);

  console.log('[PORTFOLIO_MGMT] Current state:', {
    selectedActorId,
    selectedPortfolioId,
    portfolioCount: portfolioManagement.portfolios?.length || 0,
    isEditing,
    selectedPortfolio: selectedPortfolio?.name
  });

  // Initialize portfolios when actor is selected
  useEffect(() => {
    if (selectedActorId && (!portfolioManagement.portfolios || portfolioManagement.portfolios.length === 0)) {
      console.log('[PORTFOLIO_MGMT] Initializing portfolios for actor:', selectedActorId);
      portfolioManagement.actions.initializeForActor(selectedActorId);
    }
  }, [selectedActorId, portfolioManagement.actions, portfolioManagement.portfolios]);

  // Handle actor selection
  const handleActorSelect = useCallback(async (actorId) => {
    console.log('[PORTFOLIO_MGMT] Actor selected:', actorId);
    setIsEditing(false);

    try {
      await portfolioManagement.actions.initializeForActor(actorId);
    } catch (error) {
      console.error('[PORTFOLIO_MGMT] Error initializing for actor:', error);
    }
  }, [portfolioManagement.actions]);

  // Handle portfolio selection
  const handlePortfolioSelect = useCallback(async (portfolio) => {
    console.log('[PORTFOLIO_MGMT] Portfolio selected:', portfolio.uuid, portfolio.name);
    setIsEditing(true);

    try {
      await portfolioManagement.actions.selectAndLoadPortfolio(portfolio.uuid);
    } catch (error) {
      console.error('[PORTFOLIO_MGMT] Error selecting portfolio:', error);
    }
  }, [portfolioManagement.actions]);

  // Handle new portfolio creation
  const handleNewPortfolio = useCallback(() => {
    console.log('[PORTFOLIO_MGMT] Creating new portfolio');
    setIsEditing(false);

    // Clear portfolio selection
    portfolioManagement.actions.selectPortfolio(null);
  }, [portfolioManagement.actions]);

  // Handle portfolio save
  const handlePortfolioSave = useCallback(async ({ portfolioId, data }) => {
    console.log('[PORTFOLIO_MGMT] Saving portfolio:', { portfolioId, data });

    try {
      if (portfolioId) {
        // Update existing portfolio
        await portfolioManagement.actions.updatePortfolio({ portfolioId, data });
      } else {
        // Create new portfolio
        if (!selectedActorId) {
          throw new Error('No actor selected for portfolio creation');
        }
        await portfolioManagement.actions.createPortfolio({ actorId: selectedActorId, data });
      }

      // Refresh portfolios
      await portfolioManagement.actions.fetchPortfolios(selectedActorId);
    } catch (error) {
      console.error('[PORTFOLIO_MGMT] Error saving portfolio:', error);
      throw error; // Re-throw to let the form handle it
    }
  }, [portfolioManagement.actions, selectedActorId]);

  // Handle portfolio delete
  const handlePortfolioDelete = useCallback((portfolio) => {
    console.log('[PORTFOLIO_MGMT] Delete requested for portfolio:', portfolio.uuid);
    setPortfolioToDelete(portfolio);
    setShowDeleteModal(true);
  }, []);

  // Confirm portfolio deletion
  const confirmPortfolioDelete = useCallback(async () => {
    if (!portfolioToDelete) return;

    console.log('[PORTFOLIO_MGMT] Confirming delete for portfolio:', portfolioToDelete.uuid);

    try {
      await portfolioManagement.actions.deletePortfolio(portfolioToDelete.uuid);

      // Refresh portfolios
      await portfolioManagement.actions.fetchPortfolios(selectedActorId);

      // Reset state
      setIsEditing(false);
      setShowDeleteModal(false);
      setPortfolioToDelete(null);
    } catch (error) {
      console.error('[PORTFOLIO_MGMT] Error deleting portfolio:', error);
    }
  }, [portfolioToDelete, portfolioManagement.actions, selectedActorId]);

  // Memoize the portfolio list to prevent unnecessary re-renders
  const portfolioList = useMemo(() => portfolioManagement.portfolios || [], [portfolioManagement.portfolios]);

  return (
    <ManagementHeader
      title="Portfolio Management"
      error={portfolioManagement.error}
      isLoading={portfolioManagement.loading}
      width="1024px"
      isEditing={isEditing}
      navigateToComponent={isEditing ? {
        icon: "user",
        tabIndex: 2, // Character Management tab index
        entityName: "Characters"
      } : undefined}
    >
      <Grid>
        <Grid.Row>
          <Grid.Column width={6}>
            <Segment>
              {/* Actor Selector */}
              <ActorSelector
                actors={sharedData.actors || []}
                selectedActorId={selectedActorId}
                onActorSelect={handleActorSelect}
                loading={sharedData.loading?.actors}
              />

              {/* Portfolio List */}
              {selectedActorId && (
                <div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '15px',
                    marginTop: '20px'
                  }}>
                    <Header as="h3" style={{ margin: 0 }}>Portfolios</Header>
                    <Button
                      primary
                      size="small"
                      icon
                      labelPosition="left"
                      onClick={handleNewPortfolio}
                    >
                      <Icon name="add" />
                      New Portfolio
                    </Button>
                  </div>

                  <PortfoliosListEnh
                    portfolios={portfolioList}
                    selectedPortfolioId={selectedPortfolioId}
                    onSelectPortfolio={handlePortfolioSelect}
                    onDeletePortfolio={handlePortfolioDelete}
                  />
                </div>
              )}
            </Segment>
          </Grid.Column>

          <Grid.Column width={10}>
            {selectedActorId && (
              <PortfolioForm
                onSave={handlePortfolioSave}
                onDelete={handlePortfolioDelete}
                selectedPortfolio={selectedPortfolio}
                portfolioId={selectedPortfolioId}
                isEditing={isEditing}
              />
            )}
          </Grid.Column>
        </Grid.Row>
      </Grid>

      {/* Delete Confirmation Modal */}
      <Modal open={showDeleteModal} size="small">
        <Modal.Header>Delete Portfolio</Modal.Header>
        <Modal.Content>
          <p>Are you sure you want to delete this portfolio? This action cannot be undone.</p>
          {portfolioToDelete && (
            <Message warning>
              <Message.Header>You are about to delete:</Message.Header>
              <p><strong>{portfolioToDelete.name}</strong></p>
              {portfolioToDelete.description && (
                <p>{portfolioToDelete.description}</p>
              )}
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button negative onClick={confirmPortfolioDelete}>
            Delete
          </Button>
          <Button onClick={() => {
            setShowDeleteModal(false);
            setPortfolioToDelete(null);
          }}>
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </ManagementHeader>
  );
};

export default PortfolioManagementEnh;
